import { CopyIcon } from "../components/icons";
import { useState } from "react";

function Code({ children, copy }: { children: string; copy?: boolean }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (copy) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[13px]">{children}</code>
        <button type="button" onClick={handleCopy} className="text-stone-400 hover:text-stone-600 transition-colors">
          <CopyIcon className="h-3.5 w-3.5" />
        </button>
        {copied && <span className="text-xs text-stone-400">Copied</span>}
      </span>
    );
  }

  return <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[13px]">{children}</code>;
}

function Terminal({ lines }: { lines: { cmd?: string; out?: string }[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-stone-800/80 shadow-lg shadow-stone-900/5 my-3">
      <div className="flex items-center gap-1.5 border-b border-stone-800/60 bg-stone-900 px-4 py-2.5">
        <div className="h-2.5 w-2.5 rounded-full bg-stone-700" />
        <div className="h-2.5 w-2.5 rounded-full bg-stone-700" />
        <div className="h-2.5 w-2.5 rounded-full bg-stone-700" />
      </div>
      <div className="bg-stone-950 px-5 py-4 font-mono text-[13px] leading-6">
        {lines.map((line, i) => (
          <div key={i}>
            {line.cmd !== undefined && (
              <div>
                <span className="text-stone-600">$ </span>
                <span className="text-stone-200">{line.cmd}</span>
              </div>
            )}
            {line.out !== undefined && (
              <div className="text-stone-500 pl-4">{line.out}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Docs() {
  return (
      <main className="flex flex-col gap-6 max-w-[600px] mx-auto p-5 pt-28 pb-20 lg:p-10 lg:pt-32 lg:pb-32 leading-relaxed text-base">

        <p className="text-lg font-medium text-black tracking-[-0.01em]">
          Documentation
        </p>

        {/* ── Quick start ── */}
        <section className="flex flex-col gap-2">
          <p className="font-medium">Quick start</p>
          <p className="text-stone-600">
            Start with npx. Every <Code>npx skilo-cli ...</Code> run refreshes the global <Code>skilo</Code> binary in the background, and the installed command keeps itself updated too.
          </p>
          <Terminal lines={[
            { cmd: "npx skilo-cli share ./code-reviewer" },
            { out: "\u2192 skilo.xyz/s/a3xK9mP2" },
            { cmd: "skilo add skilo.xyz/s/a3xK9mP2" },
            { out: "\u2713 Installed code-reviewer" },
            { cmd: "skilo pack ./code-reviewer flrabbit/original-landing-page-builder --name \"Starter pack\"" },
            { out: "\u2192 skilo.xyz/p/abc123" },
          ]} />
          <p className="text-stone-600">
            No account required. Skilo is free and will stay free as long as we fit inside Cloudflare's free tier. Skills and share links never expire unless you set an expiration. You can claim them later with <Code>skilo claim</Code>.
          </p>
          <p className="text-stone-600">
            Want a real namespace? Run <Code copy>skilo login yaz</Code> once. Use <Code copy>skilo login --token sk_...</Code> to restore an existing account.
          </p>
          <p className="text-stone-600">
            Agents should prefer <Code copy>npx skilo-cli --json</Code> or <Code copy>https://skilo.xyz/llms.txt</Code>.
          </p>
        </section>

        {/* ── CLI commands ── */}
        <section className="flex flex-col gap-3 mt-4">
          <p className="font-medium">CLI commands</p>

          <div className="flex flex-col gap-4">
            <div>
              <p className="text-sm">
                <Code>skilo share &lt;path&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Create a shareable link for a local skill directory. Publishes the skill and returns a URL.
              </p>
              <ul className="text-stone-600 text-sm list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li><Code>--one-time</Code> &mdash; link expires after first use</li>
                <li><Code>--expires 2h</Code> &mdash; auto-expire after a duration (m, h, d)</li>
                <li><Code>--uses 5</Code> &mdash; limit total downloads</li>
                <li><Code>--password</Code> &mdash; require a password to access</li>
                <li><Code>--listed</Code> &mdash; make the underlying skill public before linking.</li>
              </ul>
              <p className="text-stone-600 text-sm mt-1">
                Local shares default to unlisted even when you are logged in.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo share &lt;tool&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Share all skills from an AI tool at once. Supported tools: <Code>claude</Code>, <Code>codex</Code>, <Code>cursor</Code>, <Code>opencode</Code>, <Code>amp</Code>, <Code>windsurf</Code>, <Code>cline</Code>, <Code>roo</Code>, or <Code>all</Code>.
              </p>
              <ul className="text-stone-600 text-sm list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li><Code>-y</Code> &mdash; skip interactive selection, share everything</li>
              </ul>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo add &lt;skill&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Install a skill or pack. Accepts a share URL, pack URL, namespace/name, .skl file, GitHub URL, or local path.
              </p>
              <ul className="text-stone-600 text-sm list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li>Auto-installs into a detected tool when exactly one is found.</li>
                <li>Prompts once in interactive runs when multiple tools are detected.</li>
                <li>Use explicit flags or <Code>SKILO_TARGETS</Code> in non-interactive environments.</li>
                <li>Tool names also work as local sources: <Code>skilo add claude --oc --all</Code>.</li>
                <li>Pack links open a checked-by-default picker. Use <Code>--only reviewer,planner</Code> or <Code>--skip debugger</Code> in headless runs.</li>
              </ul>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo sync &lt;source&gt; &lt;target&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Copy selected skills directly between tools. For example: <Code>skilo sync claude opencode</Code>.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo publish</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Publish the current directory to the registry. Reads SKILL.md for metadata.
              </p>
              <ul className="text-stone-600 text-sm list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li><Code>--listed</Code> &mdash; publish publicly under your namespace.</li>
                <li><Code>--unlisted</Code> &mdash; keep the skill off search.</li>
              </ul>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo login &lt;username&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Create a publishing identity and save an API key locally. You can also restore with <Code>skilo login --token sk_...</Code>.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo list --published</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                List skills under your logged-in namespace, including public vs unlisted visibility.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo search &lt;query&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Search the registry for skills by name or description.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo inspect &lt;skill&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                View a skill's SKILL.md, checksum, and metadata without installing.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo pack [sources...]</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Create a shareable pack from multiple skills, links, refs, or repo sources. Returns a single pack URL.
              </p>
              <ul className="text-stone-600 text-sm list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li><Code>--name &lt;name&gt;</Code> &mdash; name the pack</li>
                <li>Supports the same <Code>--one-time</Code>, <Code>--expires</Code>, <Code>--uses</Code>, <Code>--password</Code> options as share</li>
              </ul>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo export</Code> / <Code>skilo import &lt;source&gt;</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Export to a .skl file for offline sharing, or import from a .skl file, GitHub repo, or local path.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo init [name]</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Scaffold a new skill directory with a SKILL.md template.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo validate</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Check the current directory's SKILL.md for errors before publishing.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo audit [source]</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Run a trust audit on installed skills or local bundles. Checks for hardcoded secrets, prompt exfiltration, and other risks.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo deprecate &lt;skill&gt; [message]</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Mark a skill as deprecated. Requires authentication.
              </p>
            </div>

            <div>
              <p className="text-sm">
                <Code>skilo yank &lt;skill@version&gt; [reason]</Code>
              </p>
              <p className="text-stone-600 text-sm mt-1">
                Remove a specific version from the registry. Requires authentication.
              </p>
            </div>
          </div>
        </section>

        {/* ── SKILL.md ── */}
        <section className="flex flex-col gap-2 mt-4">
          <p className="font-medium">SKILL.md format</p>
          <p className="text-stone-600">
            Every skill is a directory with a SKILL.md file. The file uses YAML frontmatter for metadata and markdown for the skill content.
          </p>
          <Terminal lines={[
            { out: "---" },
            { out: "name: my-skill" },
            { out: "description: What this skill does" },
            { out: "version: 0.1.0" },
            { out: "author: your-name" },
            { out: "---" },
            { out: "" },
            { out: "# my-skill" },
            { out: "" },
            { out: "Instructions for the agent..." },
          ]} />
          <p className="text-stone-600">
            Fields: <Code>name</Code> and <Code>description</Code> are required. <Code>version</Code>, <Code>author</Code>, <Code>homepage</Code>, <Code>repository</Code>, and <Code>keywords</Code> are optional.
          </p>
        </section>

        {/* ── Trust ── */}
        <section className="flex flex-col gap-2 mt-4">
          <p className="font-medium">Trust and verification</p>
          <p className="text-stone-600">
            Every published skill has a SHA-256 checksum. The CLI verifies integrity on install.
          </p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-stone-600">
            <li><span className="font-medium text-black">Anonymous</span> &mdash; published without an account. Inspect before installing.</li>
            <li><span className="font-medium text-black">Claimed</span> &mdash; a user has claimed ownership with <Code>skilo claim</Code>.</li>
            <li><span className="font-medium text-black">Verified</span> &mdash; publisher identity confirmed. Skill is cryptographically signed.</li>
          </ul>
          <p className="text-stone-600">
            Use <Code>skilo inspect</Code> to review a skill's content and checksum before you install.
          </p>
        </section>

        {/* ── Claiming ── */}
        <section id="claim" className="flex flex-col gap-2 mt-4">
          <p className="font-medium">Claiming skills</p>
          <p className="text-stone-600">
            When you publish without being logged in, you get an anonymous namespace and a claim token. The CLI saves it to <Code>~/.skilo/claims/</Code> automatically.
          </p>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-stone-600">
            <li><span className="font-medium text-black">Publish a skill.</span> Run <Code>skilo publish</Code> without logging in. You'll get an anonymous namespace and claim token.</li>
            <li><span className="font-medium text-black">Save the token.</span> It's saved to <Code>~/.skilo/claims/</Code> and printed in the terminal.</li>
            <li><span className="font-medium text-black">Log in and claim.</span> Run <Code copy>skilo login your-name</Code>, then claim ownership:</li>
          </ol>
          <Terminal lines={[
            { cmd: "skilo claim @namespace/skill-name --token YOUR_TOKEN" },
            { out: "\u2713 Claimed skill under your-name/skill-name" },
          ]} />
          <p className="text-stone-600">
            After logging in you can also publish directly under your namespace without the anonymous claim flow.
          </p>
        </section>

        {/* ── Tool directories ── */}
        <section className="flex flex-col gap-2 mt-4">
          <p className="font-medium">Supported tools</p>
          <p className="text-stone-600">
            Skilo discovers skills from these directories:
          </p>
          <div className="overflow-x-auto">
            <table className="text-sm w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-2 pr-4 font-medium text-black">Tool</th>
                  <th className="text-left py-2 font-medium text-black">Directory</th>
                </tr>
              </thead>
              <tbody className="text-stone-600">
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Claude Code</td><td className="py-1.5 font-mono text-[13px]">~/.claude/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Codex</td><td className="py-1.5 font-mono text-[13px]">~/.agents/skills/, ~/.codex/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Cursor</td><td className="py-1.5 font-mono text-[13px]">~/.cursor/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Amp</td><td className="py-1.5 font-mono text-[13px]">~/.config/agents/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Windsurf</td><td className="py-1.5 font-mono text-[13px]">~/.codeium/windsurf/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">OpenCode</td><td className="py-1.5 font-mono text-[13px]">~/.config/opencode/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Cline</td><td className="py-1.5 font-mono text-[13px]">~/.cline/skills/</td></tr>
                <tr className="border-b border-stone-100"><td className="py-1.5 pr-4">Roo</td><td className="py-1.5 font-mono text-[13px]">~/.roo/skills/</td></tr>
                <tr><td className="py-1.5 pr-4">OpenClaw</td><td className="py-1.5 font-mono text-[13px]">~/.openclaw/skills/</td></tr>
              </tbody>
            </table>
          </div>
        </section>
        {/* ── Privacy ── */}
        <section className="flex flex-col gap-2 mt-4">
          <p className="font-medium">Privacy</p>
          <p className="text-stone-600">
            Skills are published anonymously by default and stored as unlisted until you explicitly make them public. Skilo does not log IP addresses, user agents, or referrer data. All data is stored on Cloudflare D1 and R2.
          </p>
        </section>

        {/* ── Rate limits ── */}
        <section className="flex flex-col gap-2 mt-4">
          <p className="font-medium">Rate limits</p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-stone-600">
            <li>Publishing &mdash; 50 req/hr per IP</li>
            <li>Sharing &mdash; 100 links/hr per IP</li>
            <li>Installing / resolving &mdash; 1,000 req/hr per IP</li>
            <li>Packing &mdash; 20 packs/hr per IP</li>
            <li>Password-protected links &mdash; 5 attempts/hr per link</li>
          </ul>
          <p className="text-stone-600">
            Exceeded limits return <Code>429</Code> with a <Code>retryAfter</Code> value in seconds.
          </p>
        </section>

      </main>
  );
}

export default Docs;
